import { getStoreCreditSource } from "utils/refundConfig";

const REFUND_METHOD_ALIASES = {
  bank_transfer: ["bank_transfer", "payment_link"],
  upi: ["upi"],
  coupon: ["coupon", "coupon_code"],
  gift_card: ["gift_card"],
  store_credit: ["store_credit", "credits_yard", "easyrewardz", "shopify_store_credit"],
  credits_yard: ["store_credit", "credits_yard"],
  easyrewardz: ["store_credit", "easyrewardz"],
  shopify_store_credit: ["shopify_store_credit", "store_credit"],
  refund_to_source: ["refund_to_source", "source_refund"],
};

export const getReasonFirstRefundMethods = (item, paymentBucket) => {
  const configured = item?.reason_resolution_config?.refund_methods?.[paymentBucket];
  if (!Array.isArray(configured)) return null;
  return configured;
};

export const reasonFirstMethodIsAllowed = (item, paymentBucket, method) => {
  const configured = getReasonFirstRefundMethods(item, paymentBucket);
  if (configured === null) return true;
  const aliases = REFUND_METHOD_ALIASES[method] || [method];
  return aliases.some((alias) => configured.includes(alias));
};

export const itemAllowsRefundMethod = (item, ...aliases) => {
  const configured = Array.isArray(item?.applicable_refund_modes)
    ? item.applicable_refund_modes.filter(Boolean)
    : item?.applicable_refund_modes
      ? [item.applicable_refund_modes]
      : [];
  return configured.length === 0 || aliases.some((alias) => configured.includes(alias));
};

export const getReasonFirstRefundAmount = (quoteItem) => {
  const values = [
    quoteItem?.refund_amount,
    quoteItem?.calculated_refund_amount,
  ];
  const amount = values.find((value) => value !== null && value !== undefined && Number.isFinite(Number(value)));
  return amount === undefined ? null : Number(amount);
};

const methodAliases = (method) => {
  if (method === "bank_transfer") return ["bank_transfer", "payment_link"];
  if (method === "coupon") return ["coupon", "coupon_code"];
  if (method === "refund_to_source") return ["refund_to_source", "source_refund"];
  if (method === "store_credit" || method === "credits_yard" || method === "easyrewardz") {
    return ["store_credit", "credits_yard", "easyrewardz"];
  }
  return [method];
};

export const isReasonFirstQuoteMethodAvailable = (quoteItem, method) => {
  const quotedMethods = quoteItem?.available_refund_methods;
  if (!Array.isArray(quotedMethods)) return true;
  return methodAliases(method).some((alias) => quotedMethods.includes(alias)) ||
    quotedMethods.includes(method);
};

const modeIsAvailable = (config, mode, legacyFlag) => {
  const modes = config?.refund_config?.modes;
  if (!modes) return Boolean(config?.[legacyFlag]);
  const modeConfig = modes[mode] || {};
  return modeConfig.enabled !== false && modeConfig.available !== false;
};

/** Return the merchant/order/item methods that intersect with a reason-first policy. */
export const getReasonFirstRefundMethodOptions = (
  item,
  paymentBucket,
  rmsConfig = {},
  order = {},
) => {
  const options = [];
  const configuredOrderModes = Array.isArray(order?.applicable_refund_modes)
    ? order.applicable_refund_modes.filter(Boolean)
    : order?.applicable_refund_modes
      ? [order.applicable_refund_modes]
      : [];
  const configuredItemModes = Array.isArray(item?.applicable_refund_modes)
    ? item.applicable_refund_modes.filter(Boolean)
    : item?.applicable_refund_modes
      ? [item.applicable_refund_modes]
      : [];
  const modeAllowed = (method) => {
    const aliases = methodAliases(method);
    const orderAllowed = configuredOrderModes.length === 0 || aliases.some((alias) => configuredOrderModes.includes(alias));
    const itemAllowed = configuredItemModes.length === 0 || aliases.some((alias) => configuredItemModes.includes(alias));
    return orderAllowed && itemAllowed && reasonFirstMethodIsAllowed(item, paymentBucket, method);
  };
  const cod = Boolean(order?.is_cod);
  const bankAvailable = rmsConfig?.refund_config?.modes
    ? modeIsAvailable(rmsConfig, "bank_transfer", "customer_bank_enabled")
    : cod ? Boolean(rmsConfig.collect_bank_details_for_cod_orders) : Boolean(rmsConfig.customer_bank_enabled);
  const upiAvailable = rmsConfig?.refund_config?.modes
    ? modeIsAvailable(rmsConfig, "upi", "customer_upi_enabled")
    : cod ? Boolean(rmsConfig.collect_upi_details_for_cod_orders) : Boolean(rmsConfig.customer_upi_enabled);

  if (bankAvailable && modeAllowed("bank_transfer")) options.push({ label: "Bank Account", value: "bank_transfer" });
  if (upiAvailable && modeAllowed("upi")) options.push({ label: "UPI", value: "upi" });
  if (rmsConfig.coupon_refund_enabled && modeAllowed("coupon")) options.push({ label: rmsConfig.coupon_method_name || "Coupon", value: "coupon" });
  if (rmsConfig.refund_to_source && modeAllowed("refund_to_source")) options.push({ label: "Source Account", value: "refund_to_source" });

  const storeCreditSource = getStoreCreditSource(rmsConfig);
  if (rmsConfig.store_credit && storeCreditSource && modeAllowed(storeCreditSource)) {
    options.push({ label: rmsConfig.store_credit_method_name || "Store Credit", value: storeCreditSource });
  }
  if (rmsConfig.shopify_store_credit && modeAllowed("shopify_store_credit")) {
    options.push({ label: "Shopify Store Credits", value: "shopify_store_credit" });
  }
  if (rmsConfig.gift_card && modeAllowed("gift_card")) {
    options.push({ label: rmsConfig.gift_card_method_name || "Gift Card", value: "gift_card" });
  }

  // PriceBox retains Bank Account as its compatibility fallback when no
  // merchant refund modes are exposed. Keep the initial choice screen aligned.
  if (options.length === 0 && modeAllowed("bank_transfer")) {
    options.push({ label: "Bank Account", value: "bank_transfer" });
  }

  return options;
};
